import { datadogLogs } from '@datadog/browser-logs';
import { CamelRequestError, myProfilePath, myProfileSchema } from '@onestep/camel-api';
import { useMutation } from '@tanstack/react-query';
import type { ChallengeName, CognitoUserSession } from 'amazon-cognito-identity-js';
import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import axios, { isCancel } from 'axios';
import { useLocation, useNavigate } from 'react-router';

import type { IMfaParams } from '../../auth/Login';
import useLoggedUser from '../../hooks/useLoggedUser';
import useMixpanel from '../../hooks/useMixpanel';
import useStores from '../../hooks/useStores';
import { getErrorMessage } from '../../utils/errorUtil';
import { getMissingProfileFields } from '../../utils/profileCompletion';
import { camelClient } from '../camelClient';
import { getPortalRuntimeConfig } from '../runtimeConfig';
import type { IOTPResult } from '../signUpService/types';
import { diff, getDate } from '../timeService';
import { unitOfTime } from '../timeService/types';
import { closeWebView } from './close-web-view';
import {
  CLINICIAN_APP_CALLBACK_URIS,
  CLINICIAN_BRIDGE_PARAMS_TTL_MINUTES,
  clinicianBridgeParamsStorageKey,
  LOGIN_REFUSED_ERROR,
  mobileLoginCookieKey,
} from './consts';
import { navigateToReturnTo } from './navigateToReturnTo';
import type {
  IAuthDiscoveryResult,
  IClinicianBridgeCodeResult,
  IClinicianBridgeParams,
  IStoredClinicianBridgeParams,
} from './types';
import { isSsoRequiredError, storeClinicianBridgeParams } from './utils';

interface ILoginProps {
  poolIdentity?: ILoginPoolIdentify;
  onFinish?(idToken: string): void;
  onFail?(err: string): void;
}
interface ILoginPoolIdentify {
  UserPoolId: string;
  ClientId: string;
}

enum mobileLoginFlow {
  cookie = '1',
  redirect = '2',
  redirect_page = '3',
}

type LoginLocationState = { redirectTo?: string } | null;

export const useLoginService = ({ poolIdentity, onFinish, onFail }: ILoginProps = {}) => {
  const { loginStore, signUpStore, authStore } = useStores();
  const navigate = useNavigate();
  const location = useLocation();
  const returnToParam = new URLSearchParams(location.search).get('returnTo') ?? undefined;
  const { track, trackFail, trackSuccess } = useMixpanel({
    initData: { loginService: '1', isOldPool: !!poolIdentity },
  });
  const { logout: loggedUserLogout } = useLoggedUser();
  const { cognito, env: appEnv } = getPortalRuntimeConfig();

  const pool = new CognitoUserPool(
    poolIdentity ?? {
      UserPoolId: cognito.userPoolId,
      ClientId: cognito.clientId,
    },
  );

  const recoverFromRejectedIdToken = (err: unknown, onSsoRequired?: () => void) => {
    if (isSsoRequiredError(err)) {
      track('sso_required_for_password_login');
      onSsoRequired?.();
      loginStore.setLoginWithSso({ value: true, isRequiredByOrganization: true });
      return;
    }
    window.location.replace(`/login?error=${LOGIN_REFUSED_ERROR}`);
  };

  const loginAction = useMutation({
    mutationFn: async ({ idToken }: { idToken: string }) => {
      const mixpanelAction = 'login_to_clinic';
      try {
        track(mixpanelAction);
        loginStore.setIsLoading(true);
        await axios.post(`/api/login_cognito`, { id_token: idToken });

        trackSuccess(mixpanelAction);
        return { isSessionCreated: true };
      } catch (err) {
        trackFail(mixpanelAction, { err });
        recoverFromRejectedIdToken(err);
        return { isSessionCreated: false };
      } finally {
        loginStore.setIsLoading(false);
      }
    },
  });

  const loginOTPAction = useMutation({
    mutationFn: async ({ idToken }: { idToken: string }) => {
      const mixpanelAction = 'get_otp_from_auth';
      try {
        track(mixpanelAction);
        loginStore.setIsLoading(true);

        const result = await axios.get<IOTPResult>(`/api/login_otp`, {
          params: {
            id_token: idToken,
          },
        });

        trackSuccess(mixpanelAction);

        return result.data;
      } catch (err) {
        trackFail(mixpanelAction, { err });
        recoverFromRejectedIdToken(err);
      } finally {
        loginStore.setIsLoading(false);
      }
    },
  });

  const loginClinicianBridgeV2Action = useMutation({
    mutationFn: async ({
      idToken,
      bridgeParams,
    }: {
      idToken: string;
      bridgeParams: IClinicianBridgeParams;
    }) => {
      const mixpanelAction = 'get_clinician_bridge_v2_code';
      try {
        track(mixpanelAction);
        loginStore.setIsLoading(true);

        const result = await axios.post<IClinicianBridgeCodeResult>('/api/clinician/v2/auth/code', {
          id_token: idToken,
          code_challenge: bridgeParams.codeChallenge,
        });

        trackSuccess(mixpanelAction);

        return result.data;
      } catch (err) {
        trackFail(mixpanelAction, { err });
        recoverFromRejectedIdToken(err, () => storeClinicianBridgeParams(bridgeParams));
      } finally {
        loginStore.setIsLoading(false);
      }
    },
  });

  const readClinicianBridgeParams = (): IClinicianBridgeParams | undefined => {
    const storedParamsJson = localStorage.getItem(clinicianBridgeParamsStorageKey);
    if (!storedParamsJson) {
      return undefined;
    }

    localStorage.removeItem(clinicianBridgeParamsStorageKey);

    let stored: IStoredClinicianBridgeParams;
    try {
      stored = JSON.parse(storedParamsJson);
    } catch {
      trackFail('clinician_bridge_v2_params_unparsable');
      return undefined;
    }

    const { codeChallenge, state, redirectUri, issuedAt } =
      stored ?? ({} as Partial<IStoredClinicianBridgeParams>);
    if (
      typeof codeChallenge !== 'string' ||
      typeof state !== 'string' ||
      typeof redirectUri !== 'string' ||
      typeof issuedAt !== 'number'
    ) {
      trackFail('clinician_bridge_v2_params_malformed');
      return undefined;
    }

    if (diff(getDate(issuedAt), unitOfTime.minutes) >= CLINICIAN_BRIDGE_PARAMS_TTL_MINUTES) {
      trackFail('clinician_bridge_v2_params_expired');
      return undefined;
    }

    if (!CLINICIAN_APP_CALLBACK_URIS.includes(redirectUri)) {
      trackFail('clinician_bridge_v2_redirect_uri_rejected', { redirectUri });
      return undefined;
    }

    return { codeChallenge, state, redirectUri };
  };

  const redirectToClinicianBridgeV2App = async (
    idToken: string,
    bridgeParams: IClinicianBridgeParams,
  ) => {
    const result = await loginClinicianBridgeV2Action.mutateAsync({ idToken, bridgeParams });

    if (!result) {
      return { isRedirectedToApp: false };
    }

    const callbackUrl = `${bridgeParams.redirectUri}?code=${encodeURIComponent(result.code)}&state=${encodeURIComponent(bridgeParams.state)}`;
    window.location.replace(callbackUrl);
    return { isRedirectedToApp: true };
  };

  const resetStates = () => {
    loginStore.resetLoginStates();
    signUpStore.resetSignupStates();
  };

  const isProfileIncomplete = async (): Promise<boolean> => {
    try {
      const profile = myProfileSchema.parse(await camelClient.get(myProfilePath()));
      return getMissingProfileFields(profile).length > 0;
    } catch (error) {
      if (isCancel(error)) return false;
      datadogLogs?.logger?.error('profile_completeness_check_failed', {
        status: error instanceof CamelRequestError ? error.status : undefined,
        error_message: getErrorMessage(error),
      });
      return false;
    }
  };

  const redirectToApp = async (sessionData: { idToken: string; returnTo?: string }) => {
    const clinicianBridgeParams = readClinicianBridgeParams();

    if (localStorage[mobileLoginCookieKey] == mobileLoginFlow.cookie) {
      track('saving_result_mobile_login');
      closeWebView({ id_token: sessionData.idToken });
    } else if (localStorage[mobileLoginCookieKey] === mobileLoginFlow.redirect) {
      const result = await loginOTPAction.mutateAsync({
        idToken: sessionData.idToken,
      });
      if (!result) {
        return;
      }

      const url = `onestep-${appEnv}://open/otp?uuid=${result?.userUuid}&otp=${result?.otp}`;
      window.location.replace(url);
    } else if (localStorage[mobileLoginCookieKey] === mobileLoginFlow.redirect_page) {
      const result = await loginOTPAction.mutateAsync({
        idToken: sessionData.idToken,
      });
      if (!result) {
        return;
      }

      localStorage.setItem('login_hard_redirect', `uuid=${result?.userUuid}&otp=${result?.otp}`);
      window.location.replace('/finish_login');
    } else if (clinicianBridgeParams) {
      const { isRedirectedToApp } = await redirectToClinicianBridgeV2App(
        sessionData.idToken,
        clinicianBridgeParams,
      );
      if (!isRedirectedToApp) {
        return;
      }
    } else {
      loginStore.setIsResolvingLogin(true);
      const { isSessionCreated } = await loginAction.mutateAsync({
        idToken: sessionData.idToken,
      });
      if (!isSessionCreated) {
        loginStore.setIsResolvingLogin(false);
        return;
      }

      const profileIncomplete = await isProfileIncomplete();

      if (profileIncomplete) {
        track('profile_completion_required');
        loginStore.resetChallenge();
        loginStore.setProfileStepOnComplete(() => {
          loginStore.setProfileStepOnComplete(null);
          track('redirecting_to_clinic');
          navigateToReturnTo(
            navigate,
            sessionData.returnTo ??
              (location.state as LoginLocationState)?.redirectTo ??
              returnToParam,
          );
        });
        loginStore.setIsResolvingLogin(false);
        return;
      }

      loginStore.setIsResolvingLogin(false);
      track('redirecting_to_clinic');
      navigateToReturnTo(
        navigate,
        sessionData.returnTo ?? (location.state as LoginLocationState)?.redirectTo ?? returnToParam,
      );
    }
    resetStates();
  };

  const loginWithOtp = async ({ uuid, otp }: { uuid: string; otp: string }) => {
    try {
      loginStore.setIsLoginingWithOtp(true);
      await axios.get(`/api/login_with_otp`, {
        params: { uuid, otp },
      });
      track('redirecting_to_clinic_with_otp');
      navigateToReturnTo(
        navigate,
        (location.state as LoginLocationState)?.redirectTo ?? returnToParam,
      );
    } finally {
      loginStore.setIsLoginingWithOtp(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(clinicianBridgeParamsStorageKey);
    loggedUserLogout({ cancelSource: authStore.cancelSource, preserveRedirect: false });
    if (loginStore.cognitoUsername) {
      const user = new CognitoUser({
        Username: loginStore.cognitoUsername,
        Pool: pool,
      });

      user.signOut(() => {
        navigate('/', { replace: true });
      });
    }
  };

  const findRequiredSsoLoginUrl = async (usernameOrEmail: string) => {
    const isEmailIdentifier = usernameOrEmail.includes('@');
    if (!isEmailIdentifier) {
      return undefined;
    }
    try {
      const { data } = await axios.get<IAuthDiscoveryResult>('/api/auth/discovery', {
        params: {
          username_or_email: usernameOrEmail,
          return_to: (location.state as LoginLocationState)?.redirectTo ?? returnToParam,
        },
      });
      return data.ssoRequired ? data.providers?.[0]?.url : undefined;
    } catch (err) {
      trackFail('auth_discovery', { err });
      return undefined;
    }
  };

  const loginWithPassword = ({
    username: originalUsername,
    password,
  }: {
    username: string;
    password: string;
  }) => {
    authStore.newCancelSource();
    const username = originalUsername.trim();

    const mixpanelTrackData = { username };
    const user = new CognitoUser({
      Username: username ?? '',
      Pool: pool,
    });

    const onSuccessLogin = (session: CognitoUserSession) => {
      trackSuccess('cognito_login', mixpanelTrackData);
      const sessionData = {
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
      };
      //@ts-ignore
      if (window.AndroidInterface && window.AndroidInterface.storeTokens) {
        const idToken = sessionData.idToken;
        const accessToken = sessionData.accessToken;
        const lastAuthUser = user.getUsername();
        //@ts-ignore
        window.AndroidInterface.storeTokens(idToken, accessToken, '', lastAuthUser);
      }
      if (onFinish) {
        onFinish(sessionData.idToken);
      } else {
        redirectToApp(sessionData);
      }
    };

    const startProfileCompletion = async (session: CognitoUserSession) => {
      loginStore.setIsResolvingLogin(true);
      try {
        const idToken = session.getIdToken().getJwtToken();
        const { isSessionCreated } = await loginAction.mutateAsync({ idToken });
        if (!isSessionCreated) {
          return;
        }
        if (!(await isProfileIncomplete())) {
          onSuccessLogin(session);
          return;
        }
        track('profile_completion_required');
        loginStore.resetChallenge();
        loginStore.setProfileStepOnComplete(() => {
          loginStore.setProfileStepOnComplete(null);
          onSuccessLogin(session);
        });
      } finally {
        loginStore.setIsResolvingLogin(false);
      }
    };

    const onMfaRequired = (challengeName: ChallengeName, params: IMfaParams) => {
      const mixpanelAction = 'mfa_required';
      track(mixpanelAction, mixpanelTrackData);
      loginStore.setForgotPassword({ value: false, state: undefined });
      loginStore.setChallenge({
        name: challengeName,
        params,
        onFinish(value) {
          user.sendMFACode(value, {
            onSuccess(session) {
              trackSuccess(mixpanelAction, mixpanelTrackData);
              onSuccessLogin(session);
            },
            onFailure(err) {
              loginStore.setErrorMessager(err.message);
              trackFail(mixpanelAction, { err, ...mixpanelTrackData });
            },
          });
        },
        onResend() {
          loginWithPassword({ username, password });
        },
      });
    };

    const onNewPasswordRequired = () => {
      const mixpanelAction = 'new_password_required';
      track(mixpanelAction, mixpanelTrackData);
      loginStore.setChallenge({
        name: 'NEW_PASSWORD_REQUIRED',
        onFinish(value) {
          user.completeNewPasswordChallenge(
            value,
            {},
            {
              onFailure(err) {
                loginStore.setErrorMessager(err.message);
                trackFail(mixpanelAction, { err, ...mixpanelTrackData });
              },
              onSuccess(session) {
                trackSuccess(mixpanelAction, mixpanelTrackData);
                startProfileCompletion(session).catch((err) => {
                  loginStore.setIsResolvingLogin(false);
                  loginStore.setErrorMessager(err?.message ?? 'Failed to complete login');
                });
              },
              mfaRequired: onMfaRequired,
            },
          );
        },
      });
    };

    const mixpanelAction = 'auth_user';
    track(mixpanelAction, mixpanelTrackData);

    user.setAuthenticationFlowType('USER_SRP_AUTH');
    user.authenticateUser(
      new AuthenticationDetails({
        Username: username ?? '',
        Password: password,
      }),
      {
        onSuccess(session) {
          trackSuccess(mixpanelAction, mixpanelTrackData);
          onSuccessLogin(session);
        },
        mfaRequired: onMfaRequired,
        newPasswordRequired: onNewPasswordRequired,
        onFailure(err) {
          loginStore.setErrorMessager(err.message);
          trackFail(mixpanelAction, { err, ...mixpanelTrackData });
          if (onFail) {
            onFail(err);
          }
        },
      },
    );
  };

  const login = async (credentials: { username: string; password: string }) => {
    loginStore.setIsLoading(true);
    try {
      const requiredSsoLoginUrl = await findRequiredSsoLoginUrl(credentials.username.trim());
      if (requiredSsoLoginUrl) {
        track('redirecting_to_required_sso');
        window.location.href = requiredSsoLoginUrl;
        return;
      }
    } finally {
      loginStore.setIsLoading(false);
    }
    loginWithPassword(credentials);
  };

  return {
    state: loginStore,
    redirectToApp,
    login,
    loginWithOtp,
    logout,
    loginOTPAction,
    loginClinicianBridgeV2Action,
  };
};
